import { isValidatableControl } from "../../util/guards";
import { Logger } from "../../util/logger";

const logger = new Logger("ProgressWizard");
Logger.addLogger("ProgressWizard");

/**
 * A hack to replace `scrollend` until `scrollend` becomes Baseline available (Safari is a holdout).
 * @param {function} callback 
 * @param {number} timeout 
 */
export function waitForScrollToSettle(callback, timeout = 500) {
  let lastScrollY = window.scrollY;
  let sameCount = 0;

  const interval = setInterval(() => {
    if (window.scrollY === lastScrollY) {
      sameCount++;
      if (sameCount > 2) { // Adjust threshold if needed
        clearInterval(interval);
        callback();
      }
    } else {
      lastScrollY = window.scrollY;
      sameCount = 0;
    }
  }, 50);
}

/**
 * @param {HTMLFormElement} form The form.
 * @returns {HTMLElement|null} The progress-tracker for the wizard.
 */
export function findFormTracker(form) {
	if (!form) return null;

	const tracker = form.previousElementSibling !== null && form.previousElementSibling.classList.contains("progress-tracker")
		? /** @type {HTMLElement} */ (form.previousElementSibling)
		: form.firstElementChild !== null && form.firstElementChild.classList.contains("progress-tracker")
			? /** @type {HTMLElement} */ (form.firstElementChild)
			: null;

	return tracker;
}

/**
 * Validates all controls inside the current `.step` section.
 * Includes native inputs and custom elements that implement `checkValidity()`.
 * @param {Element|string} elementOrSelector
 * @param {boolean} [report=true]
 * @param {EventListener|null} [callback=null]
 * @returns {boolean} True if all controls within the specified element are valid.
 */
export function validateControls(elementOrSelector, report = true, callback) {
  const container = typeof(elementOrSelector) === "string" ? document.querySelector(elementOrSelector) : elementOrSelector;
	if (!container) return false;

  /** @type {import("../../util/defs").ValidatableControl[]} */
  const controls = [...container.querySelectorAll(':scope *')].filter(isValidatableControl);
	if (controls.length === 0) {
		logger.warn("No validatable controls found in container", container);
		return true;
	}

	let result = true;
	if (report) {
		if (callback) {
			for (const control of controls) {
				const iv = control.checkValidity() && control.validity.valid;
				if (!iv) {
					result = false;
					//control.addEventListener("scrollend", callback, { once: true });
					control.scrollIntoView();
					// TODO: eventually replace with `scrollend`, attaching the event to `document.scrollingElement` (not triggered on actual field element).
					waitForScrollToSettle(() => {
						control.addEventListener("__tmp", callback, { once: true });
						control.dispatchEvent(new CustomEvent("__tmp"));
					});
					break;
				}
			}
		} else {
			result = controls.every(el => el.reportValidity());
		}
	} else {
		result = controls.every(el => el.checkValidity());
	}

  return result;
}

const ariaCurrentValue = "step";

/**
 * A ProgressWizard has 3 main sections:
 * 1. A `progress-tracker` class on an `<ol>` element with `progress-step`, `progress-text`, and `progress-title` classed descendants describing the sequence of steps.
 * 2. A `progress-body` class on a block element that slides content right/left as the user moves to the next/previous step, respectively. Each step should be in an element with the `step` class. The active region will have the `active` class.
 * 3. An `edit-actions` class on a block element that does not slide, but which adapts its contents to show/hide "Previous", "Next", and "Submit" buttons (+ whatever else you want to add). This element is optional.
 */
export class ProgressWizard {

	/** @type {import("./public").ProgressWizardClasses} */
	#classes = {
		tracker: "progress-tracker",
		step: "progress-step",
		stepText: "progress-text",
		stepTitle: "progress-title",
		body: "progress-body",
		stepContent: "step",
		active: "active",
		completed: "completed",
		submitted: "submitted",
		actions: "edit-actions",
	}

	/** @type {HTMLLIElement|null|undefined} The step in the progress-tracker that should begin displaying the submit button. It either has the `[data-progress-submit="true"]` attribute or it is the final step. */
	#submitStep = undefined;

	/** @type {HTMLLIElement|null|undefined} The final step in the progress-tracker if the previous step is the `#submitStep`. */
	#completeStep = undefined;

	/**
	 * Constructor. 
	 * @param {import("./public").ProgressWizardOptions} options
	 */
	constructor(options) {
		/** @type {HTMLElement} tracker The `.progress-tracker` classed element. */
		this.tracker = options.tracker;
    
		/** @type {HTMLElement} body The block element that will slide in the inline direction when the currently active step changes. */
		this.body = options.body;

		/** @type {HTMLElement|null} actions The block element that does not slide, but which dynamically updates in response to step changes. Contains actionable elements like buttons. */
		this.actions = options.actions ?? null;

		/** @type {HTMLFormElement|null} form An optional form element associated with the wizard. */
		this.form = options.form ?? null;

		this.#classes = {
			...this.#classes,
			...options.classes
		}

		/** @type {HTMLElement[]} An array of the step elements associated with the wizard. Note these default to the class `progress-step`. */
		this.steps = [];

		/** @type {HTMLElement[]} An array of the step content blocks associated with the wizard. Note these default to the class `step`. */
		this.stepContents = [];

		/** @type {number} The index of the currently active step. This step element should always have `aria-current="step"` as an attribute. */
		this.currentIndex = 0;

		/** @type {(HTMLInputElement|HTMLButtonElement)[]} An array of the step content blocks associated with the wizard. Note these default to the class `step`. */
		this.opButtons = [];
	}

	/**
	 * Initializes the wizard by scanning the DOM, validating its results, and attaching event listeners.
	 * @returns {() => void} Returns a cleanup function to detach event listeners.
	 */
	init() {
		this.steps = [...this.tracker.querySelectorAll(`:scope .${this.#classes.step}`)].filter(n => n instanceof HTMLElement);
		this.stepContents = [...this.body.querySelectorAll(`:scope .${this.#classes.stepContent}`)].filter(n => n instanceof HTMLElement);
		this.currentIndex = this.steps.findIndex(s => s.ariaCurrent === ariaCurrentValue)

		if (this.steps.length === 0) {
			logger.error(`No <li class=\"${this.#classes.step}\"> elements found for tracker: `, this.tracker);
		}
		if (this.stepContents.length === 0) {
			logger.error(`No elements with 'class=\"${this.#classes.stepContent}\"' found for body: `, this.body);
		}
		if (this.steps.length !== this.stepContents.length) {
			// Report an error for mismatched steps vs. step content, but NOT if there's just one extra "complete" step. That is acceptable.
			if (this.steps.length !== this.stepContents.length + 1 && this.completeStep == null) {
				logger.error(`The number of steps and step content blocks do not match.`, {
					tracker: this.tracker,
					body: this.body,
					steps: this.steps,
					stepContents: this.stepContents,
				});
			}
		}

		if (!this.form) return () => {};
		const _ = this.currentStepElements;

		// Catch any submit events throughout the whole form.
		this.form.addEventListener("submit", this.#onFormSubmit.bind(this));

		// Catch any clicks from non-submit/non-reset buttons or input buttons within the actions container that are attempting to drive the progress wizard's behavior.
		this.opButtons = /** @type {(HTMLInputElement|HTMLButtonElement)[]} */ ([...this.form.querySelectorAll(`:scope .${this.#classes.actions} :is(button:not([type="submit"],[type="reset"]), input[type="button"])[data-progress-op]`)]);
		this.opButtons.forEach(btn => btn.addEventListener("click", this.#onButtonClick.bind(this)));

		return () => {
			this.form?.removeEventListener("submit", this.#onFormSubmit.bind(this));
			this.opButtons.forEach(btn => btn.removeEventListener("click", this.#onButtonClick.bind(this)));
		}
	}

	/**
	 * The "Complete" step of the progress wizard. May be null if one does not exist.
	 * @returns {HTMLLIElement|null} The "Complete" step of the progress wizard.
	 */
	get submitStep() {
		if (this.steps.length === 0) return null;
		if (typeof(this.#submitStep) === "undefined") {
			// Check for an explicit submit step.
			let item = /** @type {HTMLLIElement|undefined} */ (this.steps.find(li => li.dataset.progressSubmit === "true"));

			// If not found, assume the final one is the submit step.
			if (typeof(item) === "undefined") {
				item = /** @type {HTMLLIElement} */ (this.steps.at(-1));
			}

			this.#submitStep = item ?? null;
		}
		return this.#submitStep;
	}

	/**
	 * The "Complete" step of the progress wizard. May be null if one does not exist.
	 * @returns {HTMLLIElement|null} The "Complete" step of the progress wizard.
	 */
	get completeStep() {
		if (typeof(this.#completeStep) === "undefined") {
			const submit = this.submitStep;
			if (submit == null || submit.nextElementSibling == null || !(submit.nextElementSibling instanceof HTMLLIElement)) {
				this.#completeStep = null;
			} else  {
				this.#completeStep = submit.nextElementSibling;
			}
		}
		return this.#completeStep;
	}

	/**
	 * Transforms the wizard's tracker (`progress-tracker` class by default) and body (`progress-body` class by default) when an input with the `[data-progress-op]` attribute is clicked.
	 * When advancing to the next step, form controls within the step are validated & report errors and the current step is marked as completed.
	 * @param {SubmitEvent} event 
	 */
	#onFormSubmit(event) {
		if (!event.submitter) return;
		this.#tryOperation(event.submitter.dataset.progressOp, event.submitter, event);
	}

	/**
	 * A variation of the `onFormSubmit` callback to handle the rarer, but equally usable scenario of the wizard reacting to a buttons like this: <button type="button" data-progress-op="next">Next</button>
	 * @param {Event} event 
	 */
	#onButtonClick(event) {
		if (event.target instanceof HTMLButtonElement || (event.target instanceof HTMLInputElement && event.target.type === "button")) {
			this.#tryOperation(event.target.dataset.progressOp, event.target, event);
		}
	}

	/**
	 * 
	 * @param {import("./public").ProgressWizardOperation | null | undefined | string | HTMLElement} op
	 * @param {HTMLElement|null} element
	 * @param {Event|null} event
	 */
	#tryOperation(op, element, event = null) {
		const noop = (/** @type {Event} */ _) => {};
		switch (op) {
			case null:
			case undefined:
			case "":
				return;
			case "previous": {
				event?.preventDefault();
				this.previous(noop);
			} break;
			case "next": {
				event?.preventDefault();
				this.next(noop);
			} break;
			case "submit": {
				// Do normal processing behavior, validating the entire form.

				// If there is a complete step, go ahead and advance into it. Otherwise, remain at this step while it submits.
				if (this.completeStep != null) {
					this.next(noop);
				}
				
				// Prevent the user from accessing previous parts of the form once we've actually submitted.
				if (element != null && !element.classList.contains(this.#classes.submitted)) {
					element.classList.add(this.#classes.submitted);
					element.addEventListener("click", ev => ev.preventDefault());

					const previousButton = this.opButtons.find(el => el.dataset.progressOp === "previous");
					if (previousButton) {
						previousButton.disabled = true;
						previousButton.ariaDisabled = "true";
					}
				}
				return;
			}
		}
	}

	/**
	 * @returns {[HTMLLIElement|null, HTMLElement|null]}
	 */
	get currentStepElements() {
		if (this.steps.length === 0 && this.stepContents.length === 0) return [null, null];

		let step = /** @type {HTMLLIElement} */ (this.steps.find(s => s.ariaCurrent == "step"));
		let stepContent = /** @type {HTMLElement} */ (this.stepContents.find(s => s.classList.contains("active")));
		if (step && stepContent) return [step, stepContent];

		if ((!step || !stepContent) && this.steps.length !== 0 && this.steps[0] instanceof HTMLLIElement
			&& this.stepContents.length !== 0 && this.stepContents[0] instanceof HTMLElement
		) {
			step = this.steps[0];
			stepContent = this.stepContents[0];
			this.#setCurrent(step, stepContent);
		} else {
			logger.error("Could not determine current steps.", {
				tracker: this.tracker,
				body: this.body,
				step,
				stepContent,
			});
		}

		return [step, stepContent];
	}

	/**
	 * @param {HTMLElement} step
	 * @param {HTMLElement} stepContent
	 * @returns {void}
	 */
	#setCurrent(step, stepContent) {
		step.ariaCurrent = ariaCurrentValue;
		stepContent.classList.add(this.#classes.active);
	}

	/**
	 * @param {HTMLElement} step
	 * @param {HTMLElement} stepContent
	 * @returns {void}
	 */
	#removeCurrent(step, stepContent) {
		step.ariaCurrent = null;
		stepContent.classList.remove(this.#classes.active);
	}

	/**
	 * @param {HTMLLIElement} step 
	 * @returns {void}
	 */
	#markCompleted(step) {
		if (!step.classList.contains(this.#classes.completed)) {
			step.classList.add(this.#classes.completed);
		}
	}

	/**
	 * Moves the current step to the next step if a next one exists and the current step's controls are valid.
	 * CSS transitions should handle animation of the `.progress-body` element.
	 * @param {EventListener|null} [callback=null]
	 * @returns {void}
	 */
	next(callback = null) {
		const [currentStep, currentStepContent] = this.currentStepElements;
		if (!currentStep || !currentStepContent) {
			return;
		}
		const stepSibling = currentStep.nextElementSibling;
		const stepContentSibling = currentStepContent.nextElementSibling;
		const nextContent = stepSibling !== null && stepSibling === this.completeStep && stepContentSibling === null
			? currentStepContent
			: stepContentSibling;

		if (!this.#isStepElement(stepSibling) || !this.#isStepContentElement(nextContent)) {
			logger.error("Tried to move to next step, but one was not found.");
			return;
		}
		const onScrollEnd = async (/** @type {Event} */ ev) => {
			const element = ev.target;
			if (element instanceof HTMLElement && "reportValidity" in element && typeof(element.reportValidity) === "function") {
					element.reportValidity();
			}
		}
		if (!validateControls(currentStepContent, true, onScrollEnd)) return;

		this.#markCompleted(currentStep);
		if (nextContent !== currentStepContent) {
			currentStepContent.addEventListener("transitionend", () => {
				nextContent.classList.remove("next");
				currentStepContent.classList.remove("start");
				nextContent.classList.remove("start");

				this.#removeCurrent(currentStep, currentStepContent);
				this.#setCurrent(stepSibling, nextContent);
				this.currentIndex++;

				if (callback) {
					this.#scrollToTopOfForm(callback);
				}
			}, { once: true });

			nextContent.classList.add("next");
			void nextContent.offsetHeight; // Force reflow
			currentStepContent.classList.add("start");
			nextContent.classList.add("start");
		}
	}

	/**
	 * Moves the current step to the previous step if a previous one exists.
	 * CSS transitions should handle animation of the `.progress-body` element.
	 * @param {EventListener|null} [callback=null]
	 * @returns {void}
	 */
	previous(callback = null) {
		const [currentStep, currentStepContent] = this.currentStepElements;
		if (!currentStep || !currentStepContent) return;
		const stepSibling = currentStep.previousElementSibling;
		const stepContentSibling = currentStepContent.previousElementSibling;
		if (!this.#isStepElement(stepSibling) || !this.#isStepContentElement(stepContentSibling)) {
			logger.error("Tried to move to previous step, but one was not found.");
			return;
		}

		if (stepContentSibling !== currentStepContent) {
			currentStepContent.addEventListener("transitionend", () => {
				stepContentSibling.classList.remove("previous");
				currentStepContent.classList.remove("end");
				stepContentSibling.classList.remove("end");

				this.#removeCurrent(currentStep, currentStepContent);
				this.#setCurrent(stepSibling, stepContentSibling);
				this.currentIndex--;

				if (callback) {
					this.#scrollToTopOfForm(callback);
				}
			}, { once: true });

			stepContentSibling.classList.add("previous");
			void stepContentSibling.offsetHeight; // Force reflow
			currentStepContent.classList.add("end");
			stepContentSibling.classList.add("end");
		}
	}

	/**
	 * @param {Element} element 
	 * @param {EventListener|null} [callback=null]
	 * @returns {void}
	 */
	navigateToStepWithElement(element, callback) {
		const step = element?.closest(".step");
		if (!step || !(step instanceof HTMLElement)) return;
		if (step.classList.contains(this.#classes.active)) return; // Already there.
		const index = this.stepContents.findIndex(elem => elem === step);
		if (index === -1) {
			logger.warn("The element is not in any step known by this ProgressWizard.", element);
		}
		if (this.currentIndex === index && callback) {
			//element.addEventListener("scrollend", callback, { once: true });
			element.scrollIntoView();
			// TODO: eventually replace with `scrollend`, attaching the event to `document.scrollingElement` (not triggered on actual field element).
			waitForScrollToSettle(() => {
				element.addEventListener("__tmp", callback, { once: true });
				element.dispatchEvent(new CustomEvent("__tmp"));
			});
		} else {
			while (this.currentIndex > index) {
				const cb = this.currentIndex + 1 === index ? callback : null;
				this.previous(cb);
			}
			// Only allow fast-forward to future step to display error IF the user has already completed that step.
			while (this.currentIndex < index && this.steps.length > this.currentIndex + 1 && this.steps[this.currentIndex + 1].classList.contains(this.#classes.completed)) {
				const cb = this.currentIndex - 1 === index ? callback : null;
				this.next(cb);
			}
		}
	}

	/**
	 * @param {EventListener|null} [callback=null]
	 */
	#scrollToTopOfForm(callback = null) {
		if (this.form && this.form.previousElementSibling instanceof HTMLHeadingElement) {
			this.form.previousElementSibling.scrollIntoView({ behavior: "smooth", block: "start" });
		} else {
			const padding = 16;
			const yOffset = this.tracker.offsetTop - padding;
			if (callback) {
				window.addEventListener("scrollend", callback, { once: true })
			}
			window.scrollTo({ top: yOffset, behavior: "smooth" });
		}
	}

	/**
	 * @param {Element|null} element 
	 * @returns {element is HTMLLIElement}
	 */
	#isStepElement(element) {
		return element instanceof HTMLLIElement && element.classList.contains(this.#classes.step);
	}

	/**
	 * @param {Element|null} element 
	 * @returns {element is HTMLElement}
	 */
	#isStepContentElement(element) {
		return element instanceof HTMLElement && element.classList.contains(this.#classes.stepContent);
	}
}

// export class HTMLProgressWizardElement extends HTMLElement {
// 	connectedCallback() {
// 		console.log("Connected, child:", this.querySelector("p"));
// 	}
// }

// window.customElements.define("progress-wizard", HTMLProgressWizardElement);
